import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useVault } from '../contexts/VaultContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, ShieldCheck, Volume2, Activity, Utensils, Dumbbell, FileText, Thermometer, Droplets, Scale, Edit2, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import EncryptionBadge from '../components/EncryptionBadge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiaryPage = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'blood_pressure';
  const { t, speak, language } = useLanguage();
  const { api, isUnlocked } = useVault();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialType);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // For delete confirmation
  
  // Blood Pressure Form
  const [bpForm, setBpForm] = useState({
    systolic: '',
    diastolic: '',
    pulse: '',
    notes: '',
    entry_date: new Date().toISOString().split('T')[0]
  });
  
  // Food Form
  const [foodForm, setFoodForm] = useState({
    meal: '',
    items: '',
    calories: '',
    notes: '',
    entry_date: new Date().toISOString().split('T')[0]
  });
  
  // Exercise Form
  const [exerciseForm, setExerciseForm] = useState({
    activity_type: '',
    duration: '',
    calories_burned: '',
    notes: '',
    entry_date: new Date().toISOString().split('T')[0]
  });
  
  // Notes Form
  const [notesForm, setNotesForm] = useState({
    text: '',
    entry_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isUnlocked) {
      fetchEntries();
    }
  }, [activeTab, isUnlocked]);

  const fetchEntries = async () => {
    try {
      // Use encrypted API - data will be auto-decrypted
      const entryType = activeTab === 'notes' ? null : activeTab;
      const data = await api.getDiaryEntries(null, entryType);
      
      // Filter for notes tab
      if (activeTab === 'notes') {
        setEntries(data.filter(e => 
          ['note', 'temperature', 'blood_sugar', 'weight', 'pulse', 'oxygen'].includes(e.entry_type)
        ));
      } else {
        setEntries(data.filter(e => e.entry_type === activeTab));
      }
    } catch (error) {
      toast.error('Failed to load diary entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload = { entry_type: activeTab, entry_date: '', data: {}, notes: '' };
    
    if (activeTab === 'blood_pressure') {
      payload.entry_date = bpForm.entry_date;
      payload.data = { systolic: parseInt(bpForm.systolic), diastolic: parseInt(bpForm.diastolic), pulse: parseInt(bpForm.pulse) || null };
      payload.notes = bpForm.notes;
    } else if (activeTab === 'food') {
      payload.entry_date = foodForm.entry_date;
      payload.data = { meal: foodForm.meal, items: foodForm.items, calories: parseInt(foodForm.calories) || null };
      payload.notes = foodForm.notes;
    } else if (activeTab === 'exercise') {
      payload.entry_date = exerciseForm.entry_date;
      payload.data = { activity_type: exerciseForm.activity_type, duration: parseInt(exerciseForm.duration), calories_burned: parseInt(exerciseForm.calories_burned) || null };
      payload.notes = exerciseForm.notes;
    } else if (activeTab === 'notes') {
      payload.entry_type = 'note';
      payload.entry_date = notesForm.entry_date;
      payload.data = { text: notesForm.text };
      payload.notes = notesForm.text;
    }
    
    try {
      if (editingEntry) {
        // Update existing entry using encrypted API
        await api.updateDiaryEntry(editingEntry.id, payload);
        toast.success(language === 'hi' ? 'अपडेट हो गया!' : 'Entry updated!');
      } else {
        // Create new entry using encrypted API
        await api.createDiaryEntry(payload);
        toast.success(t('recordAdded'));
      }
      setDialogOpen(false);
      setEditingEntry(null);
      resetForms();
      fetchEntries();
    } catch (error) {
      toast.error(editingEntry ? 'Failed to update entry' : 'Failed to add entry');
    }
  };

  const resetForms = () => {
    const today = new Date().toISOString().split('T')[0];
    setBpForm({ systolic: '', diastolic: '', pulse: '', notes: '', entry_date: today });
    setFoodForm({ meal: '', items: '', calories: '', notes: '', entry_date: today });
    setExerciseForm({ activity_type: '', duration: '', calories_burned: '', notes: '', entry_date: today });
    setNotesForm({ text: '', entry_date: today });
    setEditingEntry(null);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    
    // Pre-fill form based on entry type
    if (entry.entry_type === 'blood_pressure') {
      setActiveTab('blood_pressure');
      setBpForm({
        systolic: entry.data?.systolic?.toString() || '',
        diastolic: entry.data?.diastolic?.toString() || '',
        pulse: entry.data?.pulse?.toString() || '',
        notes: entry.notes || '',
        entry_date: entry.entry_date || new Date().toISOString().split('T')[0]
      });
    } else if (entry.entry_type === 'food') {
      setActiveTab('food');
      setFoodForm({
        meal: entry.data?.meal || '',
        items: entry.data?.items || '',
        calories: entry.data?.calories?.toString() || '',
        notes: entry.notes || '',
        entry_date: entry.entry_date || new Date().toISOString().split('T')[0]
      });
    } else if (entry.entry_type === 'exercise') {
      setActiveTab('exercise');
      setExerciseForm({
        activity_type: entry.data?.activity_type || '',
        duration: entry.data?.duration?.toString() || '',
        calories_burned: entry.data?.calories_burned?.toString() || '',
        notes: entry.notes || '',
        entry_date: entry.entry_date || new Date().toISOString().split('T')[0]
      });
    } else {
      // Notes and other types
      setActiveTab('notes');
      setNotesForm({
        text: entry.data?.text || entry.notes || '',
        entry_date: entry.entry_date || new Date().toISOString().split('T')[0]
      });
    }
    
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    setDeleteConfirmId(id);
  };
  
  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await api.deleteDiaryEntry(deleteConfirmId);
      toast.success(language === 'hi' ? 'हटा दिया गया!' : 'Entry deleted!');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const renderForm = () => {
    if (activeTab === 'blood_pressure') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('systolic')}</Label>
              <Input
                type="number"
                value={bpForm.systolic}
                onChange={(e) => setBpForm({ ...bpForm, systolic: e.target.value })}
                placeholder="120"
                required
                data-testid="systolic-input"
              />
            </div>
            <div>
              <Label>{t('diastolic')}</Label>
              <Input
                type="number"
                value={bpForm.diastolic}
                onChange={(e) => setBpForm({ ...bpForm, diastolic: e.target.value })}
                placeholder="80"
                required
                data-testid="diastolic-input"
              />
            </div>
            <div>
              <Label>{t('pulse')}</Label>
              <Input
                type="number"
                value={bpForm.pulse}
                onChange={(e) => setBpForm({ ...bpForm, pulse: e.target.value })}
                placeholder="72"
                data-testid="pulse-input"
              />
            </div>
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={bpForm.entry_date}
              onChange={(e) => setBpForm({ ...bpForm, entry_date: e.target.value })}
              required
              data-testid="bp-date-input"
            />
          </div>
          <div>
            <Label>{t('notes')}</Label>
            <Textarea
              value={bpForm.notes}
              onChange={(e) => setBpForm({ ...bpForm, notes: e.target.value })}
              placeholder="How are you feeling?"
              data-testid="bp-notes-input"
            />
          </div>
        </div>
      );
    } else if (activeTab === 'food') {
      return (
        <div className="space-y-4">
          <div>
            <Label>{t('meal')}</Label>
            <Input
              value={foodForm.meal}
              onChange={(e) => setFoodForm({ ...foodForm, meal: e.target.value })}
              placeholder="e.g., Breakfast, Lunch, Dinner"
              required
              data-testid="meal-input"
            />
          </div>
          <div>
            <Label>Food Items</Label>
            <Textarea
              value={foodForm.items}
              onChange={(e) => setFoodForm({ ...foodForm, items: e.target.value })}
              placeholder="e.g., Roti, Dal, Rice, Sabzi"
              required
              data-testid="food-items-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('calories')} (approx)</Label>
              <Input
                type="number"
                value={foodForm.calories}
                onChange={(e) => setFoodForm({ ...foodForm, calories: e.target.value })}
                placeholder="500"
                data-testid="calories-input"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={foodForm.entry_date}
                onChange={(e) => setFoodForm({ ...foodForm, entry_date: e.target.value })}
                required
                data-testid="food-date-input"
              />
            </div>
          </div>
          <div>
            <Label>{t('notes')}</Label>
            <Textarea
              value={foodForm.notes}
              onChange={(e) => setFoodForm({ ...foodForm, notes: e.target.value })}
              placeholder="Any specific observations?"
              data-testid="food-notes-input"
            />
          </div>
        </div>
      );
    } else if (activeTab === 'exercise') {
      return (
        <div className="space-y-4">
          <div>
            <Label>{t('activityType')}</Label>
            <Input
              value={exerciseForm.activity_type}
              onChange={(e) => setExerciseForm({ ...exerciseForm, activity_type: e.target.value })}
              placeholder="e.g., Walking, Yoga, Running"
              required
              data-testid="activity-type-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('duration')}</Label>
              <Input
                type="number"
                value={exerciseForm.duration}
                onChange={(e) => setExerciseForm({ ...exerciseForm, duration: e.target.value })}
                placeholder="30"
                required
                data-testid="duration-input"
              />
            </div>
            <div>
              <Label>Calories Burned</Label>
              <Input
                type="number"
                value={exerciseForm.calories_burned}
                onChange={(e) => setExerciseForm({ ...exerciseForm, calories_burned: e.target.value })}
                placeholder="150"
                data-testid="calories-burned-input"
              />
            </div>
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={exerciseForm.entry_date}
              onChange={(e) => setExerciseForm({ ...exerciseForm, entry_date: e.target.value })}
              required
              data-testid="exercise-date-input"
            />
          </div>
          <div>
            <Label>{t('notes')}</Label>
            <Textarea
              value={exerciseForm.notes}
              onChange={(e) => setExerciseForm({ ...exerciseForm, notes: e.target.value })}
              placeholder="How did you feel?"
              data-testid="exercise-notes-input"
            />
          </div>
        </div>
      );
    } else if (activeTab === 'notes') {
      return (
        <div className="space-y-4">
          <div>
            <Label>{language === 'hi' ? 'आज का नोट' : 'Today\'s Note'}</Label>
            <Textarea
              value={notesForm.text}
              onChange={(e) => setNotesForm({ ...notesForm, text: e.target.value })}
              placeholder={language === 'hi' ? 'कुछ भी लिखें... जैसे: 2 कप चाय पी, ओरिगेनो ऑयल लिया, etc.' : 'Write anything... e.g., Had 2 cups tea, took oregano oil, etc.'}
              className="min-h-[100px]"
              required
              data-testid="notes-text-input"
            />
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={notesForm.entry_date}
              onChange={(e) => setNotesForm({ ...notesForm, entry_date: e.target.value })}
              required
              data-testid="notes-date-input"
            />
          </div>
        </div>
      );
    }
  };

  const renderEntry = (entry) => {
    if (entry.entry_type === 'blood_pressure') {
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-red-100">
            <Activity className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold font-['Manrope'] text-[#0D2340]">
              {entry.data.systolic}/{entry.data.diastolic} <span className="text-base font-normal text-slate-500">mmHg</span>
            </p>
            {entry.data.pulse && <p className="text-sm text-slate-500">{t('pulse')}: {entry.data.pulse} bpm</p>}
            {entry.notes && <p className="text-sm text-slate-600 mt-1">{entry.notes}</p>}
          </div>
        </div>
      );
    } else if (entry.entry_type === 'food') {
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-yellow-100">
            <Utensils className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold font-['Manrope']">{entry.data.meal}</p>
            <p className="text-sm text-slate-600">{entry.data.items}</p>
            {entry.data.calories && <Badge variant="outline" className="mt-1">{entry.data.calories} cal</Badge>}
            {entry.notes && <p className="text-sm text-slate-500 mt-1">{entry.notes}</p>}
          </div>
        </div>
      );
    } else if (entry.entry_type === 'exercise') {
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-green-100">
            <Dumbbell className="h-6 w-6 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold font-['Manrope']">{entry.data.activity_type}</p>
            <div className="flex gap-2 mt-1">
              <Badge variant="outline">{entry.data.duration} mins</Badge>
              {entry.data.calories_burned && <Badge variant="outline">{entry.data.calories_burned} cal burned</Badge>}
            </div>
            {entry.notes && <p className="text-sm text-slate-500 mt-1">{entry.notes}</p>}
          </div>
        </div>
      );
    } else if (entry.entry_type === 'temperature') {
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-orange-100">
            <Thermometer className="h-6 w-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold font-['Manrope'] text-orange-600">
              {entry.data.value}°F
            </p>
            {entry.notes && <p className="text-sm text-slate-600 mt-1">{entry.notes}</p>}
          </div>
        </div>
      );
    } else if (entry.entry_type === 'blood_sugar') {
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-pink-100">
            <Droplets className="h-6 w-6 text-pink-600" />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold font-['Manrope'] text-pink-600">
              {entry.data.level} <span className="text-base font-normal text-slate-500">mg/dL</span>
            </p>
            {entry.data.timing && <Badge variant="outline">{entry.data.timing}</Badge>}
            {entry.notes && <p className="text-sm text-slate-600 mt-1">{entry.notes}</p>}
          </div>
        </div>
      );
    } else if (entry.entry_type === 'weight') {
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-blue-100">
            <Scale className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold font-['Manrope'] text-blue-600">
              {entry.data.value} <span className="text-base font-normal text-slate-500">kg</span>
            </p>
            {entry.notes && <p className="text-sm text-slate-600 mt-1">{entry.notes}</p>}
          </div>
        </div>
      );
    } else {
      // Generic note entry
      return (
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-slate-100">
            <FileText className="h-6 w-6 text-slate-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-800">{entry.notes || entry.data?.text || 'Note'}</p>
            {entry.entry_type !== 'note' && (
              <Badge variant="outline" className="mt-1">{entry.entry_type}</Badge>
            )}
          </div>
        </div>
      );
    }
  };

  const tabConfig = [
    { value: 'blood_pressure', label: t('bloodPressure'), icon: Activity },
    { value: 'food', label: t('foodDiary'), icon: Utensils },
    { value: 'exercise', label: t('exercise'), icon: Dumbbell },
    { value: 'notes', label: 'Notes', icon: FileText },
  ];

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-purple-100">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('diary')}</h1>
              <div className="flex items-center gap-2">
                <p className="text-slate-500">{entries.length} entries</p>
                <EncryptionBadge variant="compact" />
              </div>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingEntry(null);
              resetForms();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0D2340]" data-testid="add-diary-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('addEntry')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingEntry 
                    ? (language === 'hi' ? 'एंट्री संपादित करें' : 'Edit Entry')
                    : t('addEntry')
                  }
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {renderForm()}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => {
                    setDialogOpen(false);
                    setEditingEntry(null);
                    resetForms();
                  }} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="save-diary-btn">
                    {editingEntry 
                      ? (language === 'hi' ? 'अपडेट करें' : 'Update')
                      : t('save')
                    }
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            {tabConfig.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} data-testid={`tab-${tab.value}`} className="flex gap-1 text-xs sm:text-sm">
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('noData')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className="card-interactive" data-testid={`diary-card-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {renderEntry(entry)}
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs text-slate-400">{entry.entry_date}</span>
                        {entry.is_encrypted ? (
                          <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
                            <Lock className="h-3 w-3" />
                            Encrypted
                          </Badge>
                        ) : (
                          <Badge className="blockchain-badge text-xs">
                            <ShieldCheck className="h-3 w-3" />
                            {entry.blockchain_hash?.slice(0, 8)}...
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(entry)}
                        className="text-blue-500 hover:text-blue-700"
                        data-testid={`edit-diary-${entry.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (entry.entry_type === 'blood_pressure') {
                            speak(`Blood pressure ${entry.data.systolic} over ${entry.data.diastolic}`);
                          } else if (entry.entry_type === 'food') {
                            speak(`${entry.data.meal}: ${entry.data.items}`);
                          } else {
                            speak(`${entry.data.activity_type} for ${entry.data.duration} minutes`);
                          }
                        }}
                        data-testid={`speak-diary-${entry.id}`}
                      >
                        <Volume2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-diary-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {language === 'hi' ? 'क्या आप वाकई हटाना चाहते हैं?' : 'Delete this entry?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-500 text-sm">
            {language === 'hi' ? 'यह क्रिया पूर्ववत नहीं की जा सकती।' : 'This action cannot be undone.'}
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1">
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button onClick={confirmDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white">
              {language === 'hi' ? 'हटाएं' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default DiaryPage;
